import React, { useState, useEffect, useRef } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  AlertCircle,
  Clock,
  Upload,
  Camera,
  Eye,
  RotateCcw,
  Sparkles,
  ChevronRight,
  FileText,
  UserCheck,
  CreditCard,
  Landmark,
  ArrowRight,
  RefreshCw,
  X,
  Check,
  AlertTriangle,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Info,
  Lock,
  ChevronDown
} from 'lucide-react';
import {
  SupportedDocumentType,
  CompanionDocumentRecord,
  CompanionVerificationProfile,
  DOCUMENT_TYPE_METADATA,
  verificationRequirements,
  DocumentVerificationStatus,
  VerificationOverallStatus,
} from '../../types/verification.types';
import {
  subscribeToCompanionVerification,
  subscribeToCompanionDocuments,
  uploadDocumentSide,
  submitDocumentsForReview,
  toggleBackNotApplicable,
  createDefaultDocumentRecord,
  verifyBank,
  getBankVerificationStatus,
} from '../../lib/verificationService';

interface CompanionVerificationCenterProps {
  companionId: string;
  companionName?: string;
  companionLegalName?: string;
  companionEmail?: string;
}

export const CompanionVerificationCenter: React.FC<CompanionVerificationCenterProps> = ({
  companionId,
  companionName = 'Companion',
  companionLegalName,
  companionEmail = '',
}) => {
  // Verification profile state
  const [profile, setProfile] = useState<CompanionVerificationProfile | null>(null);
  const [documents, setDocuments] = useState<Record<SupportedDocumentType, CompanionDocumentRecord>>({
    aadhaar: createDefaultDocumentRecord('aadhaar'),
    pan: createDefaultDocumentRecord('pan'),
    voter_id: createDefaultDocumentRecord('voter_id'),
    driving_license: createDefaultDocumentRecord('driving_license'),
    passport: createDefaultDocumentRecord('passport'),
  });

  // UI State
  const [activeDocType, setActiveDocType] = useState<SupportedDocumentType>('aadhaar');
  const [activeModule, setActiveModule] = useState<'identity' | 'selfie' | 'face' | 'bank'>('identity');
  const [uploadingSide, setUploadingSide] = useState<{ docType: SupportedDocumentType; side: 'front' | 'back' } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmittingAll, setIsSubmittingAll] = useState(false);

  // Preview Modal
  const [previewModal, setPreviewModal] = useState<{
    url: string;
    title: string;
    side: 'front' | 'back';
  } | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewRotation, setPreviewRotation] = useState(0);

  // Future Module: Live Camera Selfie Modal
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [capturedSelfie, setCapturedSelfie] = useState<string | null>(null);

  // Bank details remain in memory only and are sent to the authenticated backend.
  const [bankHolderName, setBankHolderName] = useState(companionLegalName || companionName);
  const [bankRegisteredPhone, setBankRegisteredPhone] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankConfirmAccount, setBankConfirmAccount] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [bankNameDetected, setBankNameDetected] = useState('');
  const [isVerifyingBank, setIsVerifyingBank] = useState(false);
  const [bankVerifiedSuccess, setBankVerifiedSuccess] = useState(false);
  const [bankVerificationState, setBankVerificationState] = useState<any>(null);
  const [bankStatusLoading, setBankStatusLoading] = useState(true);
  const [bankStatusLoadFailed, setBankStatusLoadFailed] = useState(false);

  // Hidden File Inputs
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);

  // Subscribe to real-time verification and documents
  useEffect(() => {
    if (!companionId) return;

    const unsubProfile = subscribeToCompanionVerification(companionId, (p) => {
      setProfile(p);
    });

    const unsubDocs = subscribeToCompanionDocuments(companionId, (docs) => {
      setDocuments(docs);
    });

    return () => {
      unsubProfile();
      unsubDocs();
    };
  }, [companionId]);

  useEffect(() => {
    if (!companionId) {
      setBankStatusLoadFailed(true);
      setBankStatusLoading(false);
      return;
    }
    let active = true;
    getBankVerificationStatus(companionId).then((state) => {
      if (active) {
        setBankVerificationState(state);
        setBankVerifiedSuccess(state?.status === 'verified');
      }
    }).catch(() => {
      if (active) {
        setBankVerificationState(null);
        setBankStatusLoadFailed(true);
      }
    }).finally(() => { if (active) setBankStatusLoading(false); });
    return () => { active = false; };
  }, [companionId]);

  // Handle file select
  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    docType: SupportedDocumentType,
    side: 'front' | 'back'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    setSuccessMessage(null);
    setUploadingSide({ docType, side });
    setUploadProgress(20);

    try {
      const progressTimer = setInterval(() => {
        setUploadProgress((p) => (p < 85 ? p + 20 : p));
      }, 100);

      const currentDoc = documents[docType];
      const updated = await uploadDocumentSide(companionId, docType, side, file, {
        idNumber: currentDoc.idNumber,
        legalName: currentDoc.legalName || companionName,
      });

      clearInterval(progressTimer);
      setUploadProgress(100);
      setDocuments((prev) => ({ ...prev, [docType]: updated }));
      setSuccessMessage(`✓ ${DOCUMENT_TYPE_METADATA[docType].title} (${side.toUpperCase()}) uploaded successfully!`);
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to upload document file.');
    } finally {
      setTimeout(() => {
        setUploadingSide(null);
        setUploadProgress(0);
      }, 400);
      e.target.value = '';
    }
  };

  // Submit all uploaded documents for review
  const handleSubmitForReview = async () => {
    setIsSubmittingAll(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await submitDocumentsForReview(companionId, {
        name: companionName,
        email: companionEmail,
      });
      setSuccessMessage('✓ Verification submitted! PairUpToday Admin will review your identity documents shortly.');
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to submit verification request.');
    } finally {
      setIsSubmittingAll(false);
    }
  };

  // IFSC Auto Detect
  const handleIfscChange = (code: string) => {
    const clean = code.toUpperCase().trim();
    setBankIfsc(clean);
    if (clean.startsWith('HDFC')) setBankNameDetected('HDFC Bank');
    else if (clean.startsWith('ICIC')) setBankNameDetected('ICICI Bank');
    else if (clean.startsWith('SBIN')) setBankNameDetected('State Bank of India');
    else if (clean.startsWith('UTIB')) setBankNameDetected('Axis Bank');
    else if (clean.startsWith('PUNB')) setBankNameDetected('Punjab National Bank');
    else if (clean.length >= 4) setBankNameDetected('Scheduled Commercial Bank');
    else setBankNameDetected('');
  };

  // Bank verify test
  const handleVerifyBank = async () => {
    if (isVerifyingBank) return;
    if (!bankHolderName.trim()) {
      setErrorMessage('Please enter the Beneficiary / Account Holder Name.');
      return;
    }
    const cleanPhone = bankRegisteredPhone.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setErrorMessage('Please enter a valid 10-digit Registered Phone Number.');
      return;
    }
    if (!bankAccountNumber || bankAccountNumber !== bankConfirmAccount) {
      setErrorMessage('Account number and Retype account number do not match.');
      return;
    }
    if (!bankIfsc || bankIfsc.length < 5) {
      setErrorMessage('Please enter a valid IFSC code.');
      return;
    }

    setIsVerifyingBank(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const result = await verifyBank(companionId, {
        accountHolderName: bankHolderName,
        accountNumber: bankAccountNumber,
        confirmAccountNumber: bankConfirmAccount,
        ifsc: bankIfsc,
        registeredPhone: cleanPhone,
      });
      setBankVerifiedSuccess(result.status === 'verified');
      setBankVerificationState({ status: result.status, maskedAccount: result.maskedAccount });
      if (result.status === 'verified') setSuccessMessage(`✓ Bank account verified (${result.maskedAccount}).`);
      else setErrorMessage(result.status === 'review_required'
        ? 'The account details need manual review before they can be approved.'
        : 'Verification is unavailable or could not confirm the account. Please retry later.');
    } catch (error: any) {
      setBankVerifiedSuccess(false);
      setErrorMessage(error?.message || 'Bank verification is temporarily unavailable. Please retry later.');
    } finally {
      setBankAccountNumber('');
      setBankConfirmAccount('');
      setIsVerifyingBank(false);
    }
  };

  const activeDoc = documents[activeDocType] || createDefaultDocumentRecord(activeDocType);
  const activeMeta = DOCUMENT_TYPE_METADATA[activeDocType];

  // Calculate overall counts
  const allDocList = Object.values(documents);
  const verifiedDocsCount = allDocList.filter((d) => d.status === 'verified').length;
  const pendingDocsCount = allDocList.filter((d) => d.status === 'pending').length;
  const rejectedDocsCount = allDocList.filter((d) => d.status === 'rejected' || d.status === 'reupload_required').length;
  const uploadedDocsCount = allDocList.filter((d) => d.status === 'uploaded').length;

  const progressPct = profile?.verificationProgress || (verifiedDocsCount > 0 ? 100 : pendingDocsCount > 0 ? 75 : uploadedDocsCount > 0 ? 50 : 0);
  const overallStatus = profile?.verificationStatus || (verifiedDocsCount > 0 ? 'verified' : pendingDocsCount > 0 ? 'under_review' : uploadedDocsCount > 0 ? 'submitted' : rejectedDocsCount > 0 ? 'rejected' : 'not_started');

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* 1. MAIN BANNER & PROGRESS CARD */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-[#131127] to-[#1e1338] border border-purple-900/40 p-6 sm:p-8 shadow-2xl">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-pink-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-950/80 border border-purple-500/40 text-purple-300">
                PairUpToday Verification Center
              </span>
              <span
                className={`px-3 py-1 rounded-full text-[10px] font-bold font-mono uppercase ${
                  overallStatus === 'verified'
                    ? 'bg-emerald-950 border border-emerald-500/50 text-emerald-400'
                    : overallStatus === 'under_review' || overallStatus === 'submitted'
                    ? 'bg-amber-950 border border-amber-500/50 text-amber-400'
                    : overallStatus === 'rejected'
                    ? 'bg-rose-950 border border-rose-500/50 text-rose-400'
                    : 'bg-slate-800 border border-slate-700 text-slate-300'
                }`}
              >
                {overallStatus === 'verified' && '✓ VERIFIED'}
                {overallStatus === 'under_review' && '⏳ UNDER REVIEW'}
                {overallStatus === 'submitted' && '⏳ SUBMITTED'}
                {overallStatus === 'rejected' && '✕ RE-UPLOAD REQUIRED'}
                {overallStatus === 'in_progress' && '● IN PROGRESS'}
                {overallStatus === 'not_started' && '○ NOT STARTED'}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              Verification Center
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Complete your verification to activate your Companion profile. Verified companions receive priority booking requests, safety badges, and direct escrow payouts.
            </p>

            {/* Overall Progress Bar */}
            <div className="pt-2 space-y-1.5">
              <div className="flex justify-between text-xs font-semibold">
                <span className="text-slate-300">Overall Progress</span>
                <span className="text-pink-400 font-mono font-bold">{progressPct}% Completed</span>
              </div>
              <div className="w-full h-3 bg-slate-950/80 rounded-full border border-slate-800 overflow-hidden p-0.5">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-rose-500 transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Quick Submit Button */}
          <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
            {uploadedDocsCount > 0 && overallStatus !== 'under_review' && overallStatus !== 'verified' && (
              <button
                type="button"
                onClick={handleSubmitForReview}
                disabled={isSubmittingAll}
                className="px-6 py-3 rounded-2xl bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white font-bold text-xs uppercase tracking-wider transition-all shadow-lg shadow-pink-950 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {isSubmittingAll ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                <span>Submit for Review</span>
              </button>
            )}

            <div className="p-3 bg-slate-950/60 rounded-2xl border border-slate-800 text-[11px] text-slate-400 space-y-1 font-mono">
              <div className="flex justify-between">
                <span>Verified Docs:</span>
                <span className="text-emerald-400 font-bold">{verifiedDocsCount}</span>
              </div>
              <div className="flex justify-between">
                <span>Pending Review:</span>
                <span className="text-amber-400 font-bold">{pendingDocsCount}</span>
              </div>
              <div className="flex justify-between">
                <span>Re-upload Needed:</span>
                <span className="text-rose-400 font-bold">{rejectedDocsCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. SUCCESS / ERROR NOTICES */}
      {successMessage && (
        <div className="p-4 rounded-2xl bg-emerald-950/70 border border-emerald-500/50 text-emerald-300 text-xs flex items-center justify-between gap-3 animate-in fade-in">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{successMessage}</span>
          </div>
          <button type="button" onClick={() => setSuccessMessage(null)} className="text-emerald-400 hover:text-white cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="p-4 rounded-2xl bg-rose-950/70 border border-rose-500/50 text-rose-300 text-xs flex items-center justify-between gap-3 animate-in fade-in">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            <span>{errorMessage}</span>
          </div>
          <button type="button" onClick={() => setErrorMessage(null)} className="text-rose-400 hover:text-white cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 3. FOUR VERIFICATION MODULE CARDS (Phase 1 + Future Ready) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Module 1: Identity Documents (Phase 1 Active) */}
        <div
          onClick={() => setActiveModule('identity')}
          className={`p-5 rounded-3xl border transition-all cursor-pointer relative overflow-hidden flex flex-col justify-between ${
            activeModule === 'identity'
              ? 'bg-slate-900 border-pink-500/80 shadow-lg shadow-pink-950/40 ring-1 ring-pink-500/50'
              : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-2xl bg-pink-500/20 text-pink-400 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <span
              className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                verifiedDocsCount > 0
                  ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                  : pendingDocsCount > 0
                  ? 'bg-amber-950 text-amber-400 border border-amber-800'
                  : rejectedDocsCount > 0
                  ? 'bg-rose-950 text-rose-400 border border-rose-800'
                  : 'bg-slate-800 text-slate-400'
              }`}
            >
              {verifiedDocsCount > 0
                ? '✓ Verified'
                : pendingDocsCount > 0
                ? '⏳ Under Review'
                : rejectedDocsCount > 0
                ? '✕ Rejected'
                : '○ In Progress'}
            </span>
          </div>
          <div>
            <h3 className="font-bold text-white text-sm">Identity Documents</h3>
            <p className="text-[11px] text-slate-400 mt-1">
              Aadhaar, PAN, Voter ID, Driving Licence, Passport.
            </p>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-pink-400 font-semibold">
            <span>Phase 1 Active</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>

        {/* Module 2: Selfie Verification (Future Ready) */}
        <div
          onClick={() => setActiveModule('selfie')}
          className={`p-5 rounded-3xl border transition-all cursor-pointer relative overflow-hidden flex flex-col justify-between ${
            activeModule === 'selfie'
              ? 'bg-slate-900 border-purple-500/80 shadow-lg shadow-purple-950/40 ring-1 ring-purple-500/50'
              : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-2xl bg-purple-500/20 text-purple-400 flex items-center justify-center">
              <Camera className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-slate-800 text-purple-300 border border-purple-800">
              Future Module
            </span>
          </div>
          <div>
            <h3 className="font-bold text-white text-sm">Selfie Verification</h3>
            <p className="text-[11px] text-slate-400 mt-1">
              Live photograph check with ambient lighting & liveness test.
            </p>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-purple-400 font-semibold">
            <span>View Requirements</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>

        {/* Module 3: Face Verification (Future Ready) */}
        <div
          onClick={() => setActiveModule('face')}
          className={`p-5 rounded-3xl border transition-all cursor-pointer relative overflow-hidden flex flex-col justify-between ${
            activeModule === 'face'
              ? 'bg-slate-900 border-blue-500/80 shadow-lg shadow-blue-950/40 ring-1 ring-blue-500/50'
              : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-2xl bg-blue-500/20 text-blue-400 flex items-center justify-center">
              <UserCheck className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-slate-800 text-blue-300 border border-blue-800">
              KYC Provider
            </span>
          </div>
          <div>
            <h3 className="font-bold text-white text-sm">Face Verification</h3>
            <p className="text-[11px] text-slate-400 mt-1">
              Government ID photo vs Live Selfie biometric match.
            </p>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-blue-400 font-semibold">
            <span>View Architecture</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>

        {/* Module 4: Bank Verification (Future Ready) */}
        <div
          onClick={() => setActiveModule('bank')}
          className={`p-5 rounded-3xl border transition-all cursor-pointer relative overflow-hidden flex flex-col justify-between ${
            activeModule === 'bank'
              ? 'bg-slate-900 border-emerald-500/80 shadow-lg shadow-emerald-950/40 ring-1 ring-emerald-500/50'
              : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <Landmark className="w-5 h-5" />
            </div>
            <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${bankVerifiedSuccess ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-slate-800 text-slate-400'}`}>
              {bankStatusLoading ? 'Loading' : bankStatusLoadFailed || bankVerificationState?.status === 'unavailable' ? 'Unavailable' : bankVerifiedSuccess ? '✓ Verified' : bankVerificationState?.status === 'review_required' ? '⚠ Review Required' : bankVerificationState?.status === 'failed' ? 'Failed' : bankVerificationState?.status === 'pending' ? 'In Progress' : '○ Not Verified'}
            </span>
          </div>
          <div>
            <h3 className="font-bold text-white text-sm">Bank Verification</h3>
            <p className="text-[11px] text-slate-400 mt-1">
              Automated Pennyless & Escrow direct deposit validation.
            </p>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-emerald-400 font-semibold">
            <span>Configure Bank</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>

      {/* 4. ACTIVE MODULE VIEW */}
      {/* ========================================================================= */}
      {/* MODULE 1: IDENTITY DOCUMENTS DESK (PHASE 1 IMPLEMENTATION) */}
      {/* ========================================================================= */}
      {activeModule === 'identity' && (
        <div className="space-y-6">
          {/* Document Type Selector Bar */}
          <div className="p-3 bg-slate-900/90 rounded-2xl border border-slate-800 flex items-center gap-2 overflow-x-auto scrollbar-none">
            {(Object.keys(DOCUMENT_TYPE_METADATA) as SupportedDocumentType[]).map((typeKey) => {
              const meta = DOCUMENT_TYPE_METADATA[typeKey];
              const docRec = documents[typeKey];
              const isActive = activeDocType === typeKey;
              return (
                <button
                  key={typeKey}
                  type="button"
                  onClick={() => setActiveDocType(typeKey)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer flex items-center gap-2 ${
                    isActive
                      ? 'bg-gradient-to-r from-pink-600 to-rose-600 text-white shadow-md shadow-pink-950'
                      : 'bg-slate-950 text-slate-300 hover:bg-slate-800 border border-slate-800'
                  }`}
                >
                  <span>{meta.title}</span>
                  {docRec.status === 'verified' && (
                    <span className="w-2 h-2 rounded-full bg-emerald-400" title="Verified" />
                  )}
                  {docRec.status === 'pending' && (
                    <span className="w-2 h-2 rounded-full bg-amber-400" title="Pending Review" />
                  )}
                  {docRec.status === 'reupload_required' && (
                    <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" title="Re-upload Required" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Active Document Card */}
          <div className="p-6 sm:p-8 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4">
              <div>
                <div className="flex items-center gap-2.5">
                  <h2 className="text-xl font-black text-white">{activeMeta.title}</h2>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-purple-950 border border-purple-800 text-purple-300">
                    {activeMeta.badge}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{activeMeta.subtitle}</p>
                <p className="text-xs text-slate-500 mt-0.5 font-mono">{activeMeta.helpText}</p>
              </div>

              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold font-mono ${
                    activeDoc.status === 'verified'
                      ? 'bg-emerald-950 border border-emerald-500 text-emerald-400'
                      : activeDoc.status === 'pending'
                      ? 'bg-amber-950 border border-amber-500 text-amber-400'
                      : activeDoc.status === 'uploaded'
                      ? 'bg-blue-950 border border-blue-500 text-blue-400'
                      : activeDoc.status === 'reupload_required' || activeDoc.status === 'rejected'
                      ? 'bg-rose-950 border border-rose-500 text-rose-400'
                      : 'bg-slate-950 border border-slate-700 text-slate-400'
                  }`}
                >
                  {activeDoc.status === 'verified' && '✓ VERIFIED'}
                  {activeDoc.status === 'pending' && '⏳ UNDER REVIEW'}
                  {activeDoc.status === 'uploaded' && '✓ UPLOADED (READY)'}
                  {activeDoc.status === 'reupload_required' && '✕ RE-UPLOAD REQUIRED'}
                  {activeDoc.status === 'rejected' && '✕ REJECTED'}
                  {activeDoc.status === 'not_uploaded' && '○ NOT UPLOADED'}
                </span>
              </div>
            </div>

            {/* Rejection Alert Banner if Document Rejected */}
            {(activeDoc.status === 'reupload_required' || activeDoc.status === 'rejected') && (
              <div className="p-4 rounded-2xl bg-rose-950/80 border border-rose-600/60 space-y-2">
                <div className="flex items-center gap-2 text-rose-300 font-bold text-xs uppercase tracking-wider">
                  <AlertTriangle className="w-4 h-4 text-rose-400" />
                  <span>✕ Document Rejected</span>
                </div>
                <p className="text-xs text-rose-200">
                  <strong>Reason:</strong> {activeDoc.rejectionReason || 'Image is unclear. Please upload a clearer image.'}
                </p>
                {activeDoc.adminComment && (
                  <p className="text-[11px] text-rose-300/90 italic">
                    Admin Note: &ldquo;{activeDoc.adminComment}&rdquo;
                  </p>
                )}
                <p className="text-[11px] text-rose-300">
                  Please take a clear photo showing all 4 borders, without glare or blur, and re-upload below.
                </p>
              </div>
            )}

            {/* Upload Boxes: Front Side & Back Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* FRONT SIDE */}
              <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-lg bg-pink-600/20 text-pink-400 flex items-center justify-center text-xs font-bold">
                      1
                    </span>
                    <h4 className="text-sm font-bold text-white">FRONT SIDE</h4>
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                      activeDoc.front.uploaded
                        ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                        : 'bg-slate-900 text-slate-500'
                    }`}
                  >
                    {activeDoc.front.uploaded ? '✓ Front Uploaded' : 'Required'}
                  </span>
                </div>

                {/* Upload or Preview Box */}
                {activeDoc.front.uploaded && activeDoc.front.downloadUrl ? (
                  <div className="space-y-3">
                    <div className="relative rounded-xl overflow-hidden border border-slate-800 bg-slate-900 group h-44 flex items-center justify-center">
                      <img
                        src={activeDoc.front.downloadUrl}
                        alt="Document Front"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-slate-950/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setPreviewModal({
                              url: activeDoc.front.downloadUrl!,
                              title: `${activeMeta.title} — Front`,
                              side: 'front',
                            })
                          }
                          className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center gap-1 cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>Preview</span>
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400 truncate max-w-[180px]">
                        {activeDoc.front.fileName || 'front.jpg'}
                      </span>
                      <button
                        type="button"
                        onClick={() => frontInputRef.current?.click()}
                        className="text-pink-400 hover:text-pink-300 font-bold cursor-pointer"
                      >
                        Replace Front
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => frontInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-800 hover:border-pink-500/60 rounded-xl p-6 text-center transition cursor-pointer h-44 flex flex-col items-center justify-center space-y-2 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-900 group-hover:bg-pink-600/20 text-slate-400 group-hover:text-pink-400 flex items-center justify-center transition">
                      <Upload className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white group-hover:text-pink-400 transition">
                        Upload Front Side
                      </p>
                      <p className="text-[11px] text-slate-500">JPG, PNG, PDF up to 10MB</p>
                    </div>
                  </div>
                )}

                <input
                  ref={frontInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, activeDocType, 'front')}
                />
              </div>

              {/* BACK SIDE */}
              <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-lg bg-purple-600/20 text-purple-400 flex items-center justify-center text-xs font-bold">
                      2
                    </span>
                    <h4 className="text-sm font-bold text-white">BACK SIDE</h4>
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                      activeDoc.back.notApplicable
                        ? 'bg-slate-900 text-slate-400 border border-slate-800'
                        : activeDoc.back.uploaded
                        ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                        : activeDoc.back.required
                        ? 'bg-purple-950 text-purple-300 border border-purple-800'
                        : 'bg-slate-900 text-slate-500'
                    }`}
                  >
                    {activeDoc.back.notApplicable
                      ? 'Not Applicable'
                      : activeDoc.back.uploaded
                      ? '✓ Back Uploaded'
                      : activeDoc.back.required
                      ? 'Required'
                      : 'Optional'}
                  </span>
                </div>

                {/* Back side not applicable option */}
                {!activeMeta.backRequired && (
                  <div className="p-2.5 bg-slate-900/60 rounded-xl border border-slate-800/80 flex items-center justify-between text-xs">
                    <span className="text-slate-400">Back side not applicable for {activeMeta.title}</span>
                    <span className="text-emerald-400 font-bold font-mono">✓ Single-sided</span>
                  </div>
                )}

                {activeMeta.backRequired && (
                  <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={activeDoc.back.notApplicable || false}
                      onChange={async (e) => {
                        const updated = await toggleBackNotApplicable(
                          companionId,
                          activeDocType,
                          e.target.checked
                        );
                        setDocuments((prev) => ({ ...prev, [activeDocType]: updated }));
                      }}
                      className="rounded bg-slate-900 border-slate-700 text-pink-600 focus:ring-pink-500"
                    />
                    <span>Back side not applicable for my document format</span>
                  </label>
                )}

                {/* Upload or Preview Box */}
                {!activeDoc.back.notApplicable && (
                  <>
                    {activeDoc.back.uploaded && activeDoc.back.downloadUrl ? (
                      <div className="space-y-3">
                        <div className="relative rounded-xl overflow-hidden border border-slate-800 bg-slate-900 group h-44 flex items-center justify-center">
                          <img
                            src={activeDoc.back.downloadUrl}
                            alt="Document Back"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-slate-950/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                setPreviewModal({
                                  url: activeDoc.back.downloadUrl!,
                                  title: `${activeMeta.title} — Back`,
                                  side: 'back',
                                })
                              }
                              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center gap-1 cursor-pointer"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span>Preview</span>
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-400 truncate max-w-[180px]">
                            {activeDoc.back.fileName || 'back.jpg'}
                          </span>
                          <button
                            type="button"
                            onClick={() => backInputRef.current?.click()}
                            className="text-pink-400 hover:text-pink-300 font-bold cursor-pointer"
                          >
                            Replace Back
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => backInputRef.current?.click()}
                        className="border-2 border-dashed border-slate-800 hover:border-purple-500/60 rounded-xl p-6 text-center transition cursor-pointer h-44 flex flex-col items-center justify-center space-y-2 group"
                      >
                        <div className="w-10 h-10 rounded-full bg-slate-900 group-hover:bg-purple-600/20 text-slate-400 group-hover:text-purple-400 flex items-center justify-center transition">
                          <Upload className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white group-hover:text-purple-400 transition">
                            Upload Back Side
                          </p>
                          <p className="text-[11px] text-slate-500">JPG, PNG, PDF up to 10MB</p>
                        </div>
                      </div>
                    )}

                    <input
                      ref={backInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                      onChange={(e) => handleFileChange(e, activeDocType, 'back')}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Upload Progress Indicator if active */}
            {uploadingSide && uploadingSide.docType === activeDocType && (
              <div className="p-3 bg-slate-950 rounded-xl border border-purple-500/30 space-y-1">
                <div className="flex justify-between text-xs text-purple-300">
                  <span>Uploading {uploadingSide.side.toUpperCase()} side...</span>
                  <span className="font-mono">{uploadProgress}%</span>
                </div>
                <div className="w-full h-1.5 bg-slate-900 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 transition-all duration-150"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Bottom Actions */}
            <div className="pt-4 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs text-slate-400">
                {activeDoc.status === 'verified' ? (
                  <span className="text-emerald-400 font-bold">✓ This document is verified by PairUpToday Administrator.</span>
                ) : activeDoc.status === 'pending' ? (
                  <span className="text-amber-400">⏳ This document is currently under review by our safety team.</span>
                ) : (
                  <span>Ensure your photo, full name, and ID number are sharply focused without reflections.</span>
                )}
              </div>

              {activeDoc.front.uploaded && activeDoc.status !== 'verified' && activeDoc.status !== 'pending' && (
                <button
                  type="button"
                  onClick={handleSubmitForReview}
                  disabled={isSubmittingAll}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white font-bold text-xs uppercase tracking-wider transition shadow-md shadow-pink-950 flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {isSubmittingAll ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  <span>Submit Document for Review</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODULE 2: SELFIE VERIFICATION (FUTURE MODULE 2 PREVIEW) */}
      {/* ========================================================================= */}
      {activeModule === 'selfie' && (
        <div className="p-6 sm:p-8 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-6">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-black text-white">Selfie Verification</h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-purple-950 border border-purple-800 text-purple-300">
                  Future Module
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Please take a clear selfie to verify your physical identity matches your submitted government ID.
              </p>
            </div>
            <span className="text-xs font-mono font-bold px-3 py-1 rounded-full bg-slate-950 text-slate-400 border border-slate-800">
              STATUS: READY
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            {/* Requirements Box */}
            <div className="space-y-4 p-5 rounded-2xl bg-slate-950 border border-slate-800">
              <h4 className="text-sm font-bold text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span>Selfie Requirements:</span>
              </h4>
              <ul className="space-y-2 text-xs text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Face clearly visible in frame</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Good indoor lighting (no dark shadows)</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>No sunglasses or hats</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>No facial masks</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Look directly into the camera</span>
                </li>
              </ul>
            </div>

            {/* Camera Trigger Area */}
            <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800 text-center space-y-4">
              {capturedSelfie ? (
                <div className="space-y-3">
                  <div className="w-36 h-36 mx-auto rounded-full overflow-hidden border-2 border-emerald-500 shadow-xl">
                    <img src={capturedSelfie} alt="Captured Selfie" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-xs text-emerald-400 font-bold">✓ Selfie captured successfully</p>
                  <div className="flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCapturedSelfie(null)}
                      className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold cursor-pointer"
                    >
                      Retake
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSuccessMessage('✓ Selfie submitted for review!');
                        setActiveModule('identity');
                      }}
                      className="px-4 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold cursor-pointer"
                    >
                      Save Selfie
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-20 h-20 mx-auto rounded-full bg-purple-600/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
                    <Camera className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white">Live Camera Snapshot</h4>
                    <p className="text-xs text-slate-400 mt-1">
                      Automated liveness & facial biometric provider integration is pending Phase 2 activation. Submit your verification selfie and ID via the official KYC Desk (/kyc) or Module 1 Identity Verification.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setErrorMessage(
                        'Automated liveness selfie verification is not active in Phase 1. Please submit your identity document in Module 1 or via the KYC Verification Center (/kyc) for Admin KYC review.'
                      );
                    }}
                    className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-purple-950 flex items-center justify-center gap-2 mx-auto cursor-pointer"
                  >
                    <Camera className="w-4 h-4" />
                    <span>Open Camera / Capture</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODULE 3: FACE VERIFICATION (FUTURE MODULE 3 PREVIEW) */}
      {/* ========================================================================= */}
      {activeModule === 'face' && (
        <div className="p-6 sm:p-8 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-6">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-black text-white">Face Verification & Liveness</h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-blue-950 border border-blue-800 text-blue-300">
                  Approved KYC Provider
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Automated facial recognition & 3D liveness match against your official government identification.
              </p>
            </div>
            <span className="text-xs font-mono font-bold px-3 py-1 rounded-full bg-slate-950 text-slate-400 border border-slate-800">
              STATUS: PENDING
            </span>
          </div>

          {/* Architecture Pipeline Flow Diagram */}
          <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800 space-y-4">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Verification Pipeline Flow:
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-center text-xs">
              <div className="p-3 bg-slate-900 rounded-xl border border-slate-800">
                <span className="text-[10px] text-slate-500 block">Step 1</span>
                <strong className="text-white text-[11px]">Government ID</strong>
              </div>
              <div className="p-3 bg-slate-900 rounded-xl border border-slate-800">
                <span className="text-[10px] text-slate-500 block">Step 2</span>
                <strong className="text-white text-[11px]">Document Photo</strong>
              </div>
              <div className="p-3 bg-slate-900 rounded-xl border border-slate-800">
                <span className="text-[10px] text-slate-500 block">Step 3</span>
                <strong className="text-white text-[11px]">Live Selfie</strong>
              </div>
              <div className="p-3 bg-slate-900 rounded-xl border border-slate-800">
                <span className="text-[10px] text-slate-500 block">Step 4</span>
                <strong className="text-white text-[11px]">Liveness Check</strong>
              </div>
              <div className="p-3 bg-slate-900 rounded-xl border border-slate-800">
                <span className="text-[10px] text-slate-500 block">Step 5</span>
                <strong className="text-white text-[11px]">Face Match</strong>
              </div>
              <div className="p-3 bg-slate-900 rounded-xl border border-emerald-900/60 text-emerald-400">
                <span className="text-[10px] text-emerald-500 block">Result</span>
                <strong className="text-emerald-400 text-[11px]">MATCHED ✓</strong>
              </div>
            </div>

            <div className="p-4 bg-blue-950/40 rounded-xl border border-blue-900/40 text-xs text-blue-200/90 leading-relaxed">
              <div className="flex items-center gap-1.5 font-bold mb-1 text-blue-300">
                <Lock className="w-3.5 h-3.5" />
                <span>Privacy & Security Notice:</span>
              </div>
              Biometric face recognition algorithms run exclusively through certified government-authorized KYC providers on hardened cloud functions. Raw facial matching scores are never exposed directly on client devices.
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODULE 4: BANK VERIFICATION (FUTURE MODULE 4 PREVIEW) */}
      {/* ========================================================================= */}
      {activeModule === 'bank' && (
        <div className="p-6 sm:p-8 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-6">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-black text-white">Bank Account Verification</h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-emerald-950 border border-emerald-800 text-emerald-300">
                  Escrow Payouts
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Verify your account for secure direct deposit of Companion meetup earnings.
              </p>
            </div>
            <span className={`text-xs font-mono font-bold px-3 py-1 rounded-full ${bankVerificationState?.status === 'verified' ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-slate-950 text-slate-400 border border-slate-800'}`}>
              {bankStatusLoading ? 'STATUS: LOADING' : bankStatusLoadFailed ? 'STATUS: UNAVAILABLE' : bankVerificationState?.status === 'verified' ? 'STATUS: VERIFIED ✓' : bankVerificationState?.status === 'review_required' ? 'STATUS: REVIEW REQUIRED' : bankVerificationState?.status === 'unavailable' ? 'STATUS: UNAVAILABLE' : bankVerificationState?.status === 'pending' ? 'STATUS: IN PROGRESS' : bankVerificationState?.status === 'failed' ? 'STATUS: FAILED' : 'STATUS: NOT VERIFIED'}
            </span>
          </div>

          <div className="max-w-xl space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1">
                  Beneficiary / Account Holder Name (as on Bank Passbook)
                </label>
                <input
                  type="text"
                  value={bankHolderName}
                  onChange={(e) => setBankHolderName(e.target.value)}
                  placeholder="e.g. Rahul Roy"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1">
                  Registered Phone Number
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={bankRegisteredPhone}
                  onChange={(e) => setBankRegisteredPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10-digit mobile number"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-mono focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1">
                  Account Number
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={bankAccountNumber}
                  onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter full account number"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-mono focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1">
                  Retype Account Number
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={bankConfirmAccount}
                  onChange={(e) => setBankConfirmAccount(e.target.value.replace(/\D/g, ''))}
                  placeholder="Retype account number"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-mono focus:border-emerald-500 focus:outline-none"
                />
                {bankConfirmAccount && bankAccountNumber !== bankConfirmAccount && (
                  <span className="text-[10px] font-bold text-rose-400 block mt-1">
                    Account numbers do not match
                  </span>
                )}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs font-bold text-slate-300">
                  IFSC Code
                </label>
                {bankNameDetected && (
                  <span className="text-[11px] text-emerald-400 font-bold font-mono">
                    ✓ {bankNameDetected}
                  </span>
                )}
              </div>
              <input
                type="text"
                value={bankIfsc}
                onChange={(e) => handleIfscChange(e.target.value)}
                placeholder="e.g. HDFC0001234"
                className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-mono uppercase focus:border-emerald-500 focus:outline-none"
              />
            </div>

            {/* Masked Preview for Security */}
            {bankAccountNumber.length > 4 && (
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 text-xs flex justify-between items-center text-slate-400 font-mono">
                <span>Display Account:</span>
                <span className="text-white font-bold">
                  XXXXXXXX{bankAccountNumber.slice(-4)}
                </span>
              </div>
            )}
            {bankVerificationState?.maskedAccount && (
              <p className="text-[11px] text-slate-400 font-mono">Saved account: {bankVerificationState.maskedAccount}</p>
            )}

            <button
              type="button"
              onClick={handleVerifyBank}
              disabled={isVerifyingBank || bankStatusLoading || !companionId || bankVerificationState?.status === 'verified'}
              className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-emerald-950 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {isVerifyingBank ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              <span>{isVerifyingBank ? 'Checking bank details…' : bankVerificationState?.status === 'verified' ? 'Bank Account Verified ✓' : 'Verify Bank Account (Pennyless)'}</span>
            </button>
            {bankStatusLoadFailed && <p className="text-xs text-amber-300">Saved verification status could not be loaded. Check your sign-in and connection before retrying.</p>}
          </div>
        </div>
      )}

      {/* 5. FULLSCREEN DOCUMENT PREVIEW MODAL */}
      {previewModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-3xl w-full overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="font-bold text-white text-sm">{previewModal.title}</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPreviewZoom((z) => Math.min(z + 0.25, 2.5))}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewZoom((z) => Math.max(z - 0.25, 0.75))}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewRotation((r) => (r + 90) % 360)}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
                  title="Rotate"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPreviewModal(null);
                    setPreviewZoom(1);
                    setPreviewRotation(0);
                  }}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-900 text-slate-300 hover:text-white cursor-pointer ml-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-auto flex items-center justify-center bg-slate-950 flex-1 min-h-[350px]">
              <img
                src={previewModal.url}
                alt="Document Preview"
                style={{
                  transform: `scale(${previewZoom}) rotate(${previewRotation}deg)`,
                  transition: 'transform 0.2s ease-out',
                }}
                className="max-h-[60vh] max-w-full rounded-xl object-contain shadow-2xl"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
